import React from "react";
import { StyleSheet, Platform, TouchableOpacity } from "react-native";
import RText from "../RText";
import Gap from "../Gap";
import Icon from "../Icon";
import usePressableProps from "../../hooks/usePressableProps";
import { prepareParams } from "../Screens/Results/utils";
import Race from "../../types/races/race";
import { RaceResult } from "../../types/result";
import { RIconSize, RTextSize, RTextWeight } from "../RText/RText";

interface VideoLinkProps {
  race: Race | RaceResult;
  videoType: "replay" | "preview";
  shouldBecomeAvailable: boolean;
  openInNewTab?: boolean;
  trackSlug?: string;
  start_time_scheduled?: string;
  displayIconOnly?: boolean;
  textSize?: RTextSize;
  textWeight?: RTextWeight;
  iconSize?: RIconSize;
}
/* JSDOC
 * VideoLink component renders a link to a video (replay or preview) for a specific race.
 * It displays the video link with appropriate styling and behavior based on the props provided.
 * @param {Object} props - The component props
 * @param {Race|RaceResult} props.race - The race object containing race details
 * @param {"replay"|"preview"} props.videoType - The type of video link to display
 * @param {boolean} props.shouldBecomeAvailable - Flag indicating if the video link should be shown
 * @param {boolean} [props.openInNewTab=false] - Whether to open the link in a new tab
 * @param {string} [props.trackSlug] - Optional track slug for URL parameters
 * @param {string} [props.start_time_scheduled] - Optional scheduled start time for URL parameters
 * @param {boolean} [props.displayIconOnly=false] - Whether to display only the icon without text
 * @param {RTextSize} [props.textSize="sm"] - Size of the text to display
 * @param {RTextWeight} [props.textWeight="bold"] - Weight of the text to display
 * @param {RIconSize} [props.iconSize="xlarge"] - Size of the icon to display
 * @returns {JSX.Element|null} The rendered VideoLink component or null if not available
 */
const VideoLink: React.FC<VideoLinkProps> = ({
  race,
  videoType,
  shouldBecomeAvailable, // e.g., based on TV channel

  openInNewTab = false,
  trackSlug,
  start_time_scheduled,
  displayIconOnly = false,
  textSize = "sm",
  textWeight = "bold",
  iconSize = "xlarge",
}) => {
  const params = prepareParams({
    time: start_time_scheduled || race?.start_time_scheduled,
    track: trackSlug || race?.meeting?.track?.slug,
  });

  const replayAvailable = !!race?.replay_videos?.length;

  if (!shouldBecomeAvailable) {
    return null;
  }

  const options = {
    replayOption: {
      text: "Watch Replay",
      color: "secondary",
      iconName: "playCircled",
      pressableProps: usePressableProps({
        screen: "IndividualReplay",
        params: params,
        openInNewTab: openInNewTab,
      }),
      shouldHover: true,
    },
    previewOption: {
      text: "Watch Preview",
      color: "secondary",
      iconName: "playCircled",
      pressableProps: usePressableProps({
        screen: "IndividualRacecard",
        params: params,
        openInNewTab: openInNewTab,
      }),
      shouldHover: true,
    },
    awaitingOption: {
      text: "Awaiting Replay",
      color: "disabled",
      iconName: "playCircledUnavailable",
      pressableProps: null,
      shouldHover: false,
    },
  };

  let selectedOption;

  if (videoType == "preview") {
    selectedOption = "previewOption";
  } else {
    selectedOption = replayAvailable ? "replayOption" : "awaitingOption";
  }

  const hoverRef = React.useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (hoverRef.current && Platform.OS === "web") {
      hoverRef.current.style.transform = "scale(1.1)"; // Example hover style
    }
  };

  const handleMouseLeave = () => {
    if (hoverRef.current && Platform.OS === "web") {
      hoverRef.current.style.transform = "scale(1)"; // Revert style
    }
  };

  return (
    <TouchableOpacity
      ref={hoverRef}
      style={[styles.centeredRow, { marginRight: 10 }]}
      {...(options[selectedOption].pressableProps
        ? options[selectedOption].pressableProps
        : {})}
      onMouseEnter={
        options[selectedOption].shouldHover ? handleMouseEnter : () => {}
      }
      onMouseLeave={
        options[selectedOption].shouldHover ? handleMouseLeave : () => {}
      }
    >
      {!displayIconOnly && (
        <RText
          weight={textWeight}
          size={textSize}
          color={options[selectedOption].color}
        >
          {options[selectedOption].text}
        </RText>
      )}
      <Gap size="xsmall" />
      <Icon name={options[selectedOption].iconName} size={iconSize} />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  centeredRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  hover: {
    transform: [{ scale: 1.1 }],
  },
});

export default VideoLink;
